/**
 * Severity Scale + Scoring Defaults — leaf module (no upstream imports).
 *
 * STRAK-TREK PHASES 4 + 6 — Single source of truth for:
 *   • The 15-step semantic severity ladder used in issue construction
 *     (8 base steps named in Phase 4 + 7 interstitial `NEAR_X` steps
 *     named in Phase 6, for fine-grained pedagogical positioning).
 *   • The default scoring weights used by `computeIssuePenalty`.
 *
 * BACKGROUND
 * Per `docs/audits/UITGESTELD_C3_MAGIC_NUMBERS_NAAR_POLICY.md`, the pipeline
 * had 141+ raw `severity: N` literals scattered across content validators,
 * plus a hidden scoring formula (blocker=30, coach=severity/4, info=2) baked
 * into `computeIssuePenalty`. Those numbers are *pedagogical decisions* and
 * belong in policy.
 *
 * Authors should reach for base steps by default (CRITICAL=80, HIGH=70,
 * REVIEW=55, MEDIUM=45, LOW=35, MINOR=25, COSMETIC=15, INFO=10). The
 * `NEAR_X` interstitials (75, 60, 50, 40, 30, 20, 5) exist for cases where
 * the issue is clearly more or less serious than the base step but does
 * not warrant a full tier jump. Naming: `NEAR_X` means "approaching X
 * from below".
 *
 * Numeric values are *exact preservations* of the literals already in use
 * across stages/contentValidators/*. Do not adjust without a snapshot diff.
 *
 * IT IS A LEAF MODULE on purpose: importing it must never cause a cycle
 * with `policyEngine`, `defaultPolicy`, or `issues/builders`.
 */

// ─── Semantic severity steps (15-step ladder) ────────────────────────────────
//
// Mapping to the admin "5-bucket" display taxonomy:
//   CRITICAL (red)   ← SEVERITY.CRITICAL, SEVERITY.NEAR_CRITICAL,
//                      SEVERITY.HIGH                            (>= 70)
//   BLOCKER  (orange)← all `kind: 'blocker'` regardless of N
//   REVIEW   (yellow)← SEVERITY.NEAR_HIGH, SEVERITY.REVIEW,
//                      SEVERITY.NEAR_REVIEW                     (45–69)
//   WARNING  (blue)  ← SEVERITY.MEDIUM, SEVERITY.NEAR_MEDIUM,
//                      SEVERITY.LOW, SEVERITY.NEAR_LOW          (25–44)
//   INFO     (slate) ← SEVERITY.MINOR, SEVERITY.NEAR_MINOR,
//                      SEVERITY.COSMETIC, SEVERITY.INFO,
//                      SEVERITY.TRACE                           (< 25)
//
// PEDAGOGICAL ROLE GUIDE
// ──────────────────────
// Base steps (Phase 4) are the *default* values an author should reach for.
// `NEAR_X` interstitials (Phase 6) exist for cases where the issue is clearly
// more or less serious than the standard step, but does not warrant jumping
// a full tier. Naming convention: `NEAR_X` means "approaching X from below".
//
export const SEVERITY = {
  // ── Tier 1: blockers ──────────────────────────────────────────────────────
  /** Hard-blocking defect (default for `blocker()` factory). */
  CRITICAL: 80,
  /**
   * Severe blocker, almost critical. Use for structurally wrong content
   * that should not ship but is not catastrophic (e.g. missing required
   * pedagogical contract field at tier ≥ 8).
   */
  NEAR_CRITICAL: 75,
  /** Tier-escalated blocker (e.g. missing distractor explanation at tier ≥ 9). */
  HIGH: 70,

  // ── Tier 2: review-grade coach ────────────────────────────────────────────
  /**
   * Escalated review — more urgent than a standard review, but not a blocker.
   * Use when content needs author attention before next batch (e.g. seed-level
   * structural smell, visual mismatched to question intent).
   */
  NEAR_HIGH: 60,
  /** Severe coach — almost blocking, e.g. answer-leak in context, generic LV/MV at tier ≥ 5. */
  REVIEW: 55,
  /**
   * Soft review — one notch above standard coach. Use when an issue should
   * be addressed soon but the content remains usable (e.g. notable
   * pedagogical-depth concerns, secondary distractor problems).
   */
  NEAR_REVIEW: 50,

  // ── Tier 3: standard coach ────────────────────────────────────────────────
  /** Standard coach default (used as the fallback in `coach()` factory). */
  MEDIUM: 45,
  /**
   * Standard quality coach below the default. Use for the bulk of
   * content/notation/distractor checks where the issue is well-defined
   * and moderate (this is the most common interstitial across the pipeline).
   */
  NEAR_MEDIUM: 40,
  /** Quality-improvement coach (e.g. low didactic depth, distractor too short). */
  LOW: 35,

  // ── Tier 4: minor / cosmetic ──────────────────────────────────────────────
  /**
   * Borderline minor improvement — between LOW and MINOR. Use for small
   * quality nudges that are slightly more substantive than a pure cosmetic.
   */
  NEAR_LOW: 30,
  /** Minor coach (e.g. missing CITO discrimination on non-assessment type). */
  MINOR: 25,
  /**
   * Soft cosmetic suggestion — between MINOR and COSMETIC. Use for
   * polish-level findings that authors should be aware of but rarely need
   * to act on immediately.
   */
  NEAR_MINOR: 20,
  /** Cosmetic / nice-to-have (e.g. missing rule reference at low tier). */
  COSMETIC: 15,

  // ── Tier 5: info / trace ──────────────────────────────────────────────────
  /** Pure-info (default for `info()` factory). */
  INFO: 10,
  /**
   * Diagnostic trace — near-invisible signal. Use for pipeline introspection
   * and validator-debug breadcrumbs that should never affect gating.
   */
  TRACE: 5,
} as const

export type SeverityLevelName = keyof typeof SEVERITY

/**
 * Look up a numeric severity by its semantic name.
 * Falls back to `MEDIUM` if the name is unknown — defensive default so
 * stray strings never produce `NaN` severities in the pipeline.
 */
export function severityValue(name: SeverityLevelName | string): number {
  return (SEVERITY as Record<string, number>)[name] ?? SEVERITY.MEDIUM
}

// ─── Scoring weights ─────────────────────────────────────────────────────────
//
// Penalty formula applied by `computeIssuePenalty`:
//   total = Σ over issues:
//     blocker → +blockerPenalty                       (default 30)
//     coach   → +round(issue.severity / coachDivisor) (default ÷4)
//     info    → +infoPenalty                          (default 2)
// The resulting penalty is subtracted from 100 to yield the gate score.

export interface ScoringWeights {
  blockerPenalty: number
  coachPenaltyDivisor: number
  infoPenalty: number
}

/**
 * DEFAULT_SCORING_WEIGHTS — the exact numbers used by the pipeline since
 * inception. Centralised here so `defaultPolicy.ts` and the back-compat path
 * of `computeIssuePenalty` reference the same object.
 */
export const DEFAULT_SCORING_WEIGHTS: ScoringWeights = {
  blockerPenalty: 30,
  coachPenaltyDivisor: 4,
  infoPenalty: 2,
}
